<script setup lang="ts">
import { computed } from 'vue'

type Props = {
  dataTest?: string
  disabled?: boolean
  subtitle?: string
  title: string
  value?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  dataTest: 'core-switch',
  disabled: false,
})

type Emits = (e: 'input', value: boolean) => void
const emit = defineEmits<Emits>()

const valueHandler = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked
  emit('input', checked)
}

const buttonDataTest = computed(() => `${props.dataTest}-button`)
const subtitleDataTest = computed(() => `${props.dataTest}-subtitle`)
const titleDataTest = computed(() => `${props.dataTest}-title`)

const dataTestValue = computed(() => {
  return `${props.dataTest}-value-${props.value}`
})
</script>

<template>
  <div
    :class="{ disabled }"
    :data-test="dataTest"
    class="switch flex flex-col">
    <div class="flex items-center">
      <span
        class="switch__title body--body-01-bold grow"
        :data-test="titleDataTest">{{ title }}</span>
      <label
        class="switch__button flex-none"
        :data-test="buttonDataTest">
        <input
          class="switch__button__input"
          type="checkbox"
          :checked="value || undefined"
          :disabled="disabled"
          @input="valueHandler">
        <span
          :data-test="dataTestValue"
          class="switch__button__slider" />
      </label>
    </div>
    <span
      v-if="subtitle"
      class="switch__subtitle body--auxiliary-01 text-core-neutral-cold-50"
      :data-test="subtitleDataTest">{{ subtitle }}</span>
  </div>
</template>

<style scoped>
.switch__title {
  color: var(--core-primary-10);
}

.disabled .switch__title {
  color: var(--core-neutral-cold-50);
}

.switch__button {
  position: relative;
  display: inline-block;
  height: var(--s-6);
  width: var(--s-9);
  margin: 0;
}

.switch__button__input {
  opacity: 0;
  height: 0;
  width: 0;
}

.switch__button__slider {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 6.25rem;
  cursor: pointer;
  background-color: var(--core-neutral-cold-50);
  transition:
    background-color .4s,
    transform .4s;
}

.switch__button__slider:before {
  position: absolute;
  content: "";
  height: var(--s-5);
  width: var(--s-5);
  left: var(--s-1);
  bottom: var(--s-1);
  border-radius: 100%;
  background-color: var(--core-neutral-cold-100);
  transition:
    background-color .4s,
    transform .4s;
}

.switch__button__slider:hover {
  cursor: pointer;
  background-color: var(--core-neutral-cold-60);
}

.disabled .switch__button__slider {
  cursor: inherit;
  background-color: var(--core-neutral-cold-90) !important;
}

.disabled .switch__button__slider:before {
  background-color: var(--core-neutral-cold-80);
}

.switch__button__input:checked + .switch__button__slider {
  background-color: var(--auxiliary-success-50);
}

.switch__button__input:focus-visible + .switch__button__slider {
  outline: 3px solid var(--auxiliary-info-60);
}

.switch__button__input:checked + .switch__button__slider:before {
  transform: translateX(var(--s-5));
}

.switch:not(.disabled) .switch__button__input:active + .switch__button__slider:before {
  outline: 5px solid var(--core-neutral-cold-80);
}

.switch:not(.disabled) .switch__button__input:active:checked + .switch__button__slider:before {
  outline: 5px solid var(--auxiliary-success-70);
}

.switch__subtitle {
  color: var(--core-neutral-cold-50);
}

.disabled .switch__subtitle {
  color: var(--core-neutral-cold-80);
}
</style>
